/**
 * Node Icon Mapping
 *
 * Maps node types to their corresponding iconify icons
 * This ensures consistent iconography across the flow builder
 */

import type { Component } from 'vue'
import { resolveFlowIcon } from '../components/icons'
import type { NodeType } from '../types'

export interface NodeIconMapping {
  [key: string]: string
}

export const nodeIconMap: NodeIconMapping = {
  // Split/Question nodes
  'sg-question': 'hugeicons:message-question', // Question split
  'sg-router': 'hugeicons:flow-square', // Router split
  random: 'hugeicons:hierarchy-square-08', // Random split
  highscore: 'hugeicons:award-01', // High score split

  // Offer nodes
  'offer-form': 'hugeicons:inbox', // Form offer
  'offer-offer': 'hugeicons:gift', // Promotion offer

  // Message nodes
  message: 'hugeicons:message-01', // Submit message

  // Action nodes
  redirect: 'hugeicons:move-right', // Redirect
  code: 'hugeicons:code', // Code execution
  data: 'hugeicons:database', // Set data
  tag: 'hugeicons:tag-01', // Add tag (future)
  field: 'hugeicons:form', // Set field (future)
  webhook: 'hugeicons:webhook', // Webhook (future)

  // Flow control nodes
  yesno: 'hugeicons:filter-horizontal', // Condition
  exit: 'hugeicons:logout-04', // Exit flow
  abtest: 'hugeicons:test-tube-01' // A/B test (future)
}

/**
 * Get icon for a specific node
 */
export function getNodeIcon(node: any): Component {
  // Handle split nodes (sg type)
  if (node.type === 'sg') {
    if (node.ask) {
      return resolveFlowIcon(nodeIconMap['sg-question'])!
    } else {
      return resolveFlowIcon(nodeIconMap['sg-router'])!
    }
  }

  // Handle offer nodes
  if (node.type === 'offer') {
    if (node.desiredType === 'form') {
      return resolveFlowIcon(nodeIconMap['offer-form'])!
    } else {
      return resolveFlowIcon(nodeIconMap['offer-offer'])!
    }
  }

  // Handle other node types
  const iconKey = node.type as keyof NodeIconMapping
  return resolveFlowIcon(nodeIconMap[iconKey] || 'hugeicons:help-circle')!
}

/**
 * Get icon by node type and subtype
 */
export function getIconByType(type: string, subtype?: string): Component {
  if (type === 'sg') {
    return subtype === 'question'
      ? resolveFlowIcon(nodeIconMap['sg-question'])!
      : resolveFlowIcon(nodeIconMap['sg-router'])!
  }

  if (type === 'offer') {
    return subtype === 'form'
      ? resolveFlowIcon(nodeIconMap['offer-form'])!
      : resolveFlowIcon(nodeIconMap['offer-offer'])!
  }

  return resolveFlowIcon(nodeIconMap[type] || 'hugeicons:help-circle')!
}
